import React, { useEffect, useState, useMemo } from 'react';
import { istDateTime } from '../lib/ist';
import axios from 'axios';
import { toast } from 'sonner';
import {
  RefreshCw, RotateCcw, Receipt, CheckCircle2, X,
  AlertTriangle, TrendingUp, Banknote, Landmark, ShieldCheck, ShieldAlert,
  ArrowUp, ArrowDown,
} from 'lucide-react';
import { API, useAuth, canEdit } from '../App';
import ScrollSentinel from '../components/ScrollSentinel';

const PAGE_SIZE = 50;

const Payments = () => {
  const { token, user } = useAuth();
  const editable = canEdit(user, 'payments');
  const [tab, setTab] = useState('orders'); // orders | refunds | settlements
  const [statusFilter, setStatusFilter] = useState('paid');
  const [kioskFilter, setKioskFilter] = useState('');
  const [kiosksList, setKiosksList] = useState([]);
  const [orderSort, setOrderSort] = useState({ key: 'created_at', dir: 'desc' });
  const [orders, setOrders] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [hasMoreOrders, setHasMoreOrders] = useState(false);
  const [hasMoreRefunds, setHasMoreRefunds] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [settlements, setSettlements] = useState({ settlements: [], summary: {} });
  const [loading, setLoading] = useState(false);
  const [refundModal, setRefundModal] = useState(null); // { order }
  const [refundForm, setRefundForm] = useState({ amount: '', reason: '' });
  const [submittingRefund, setSubmittingRefund] = useState(false);
  const [orderDetail, setOrderDetail] = useState(null); // expanded refund history per order
  const [syncingSettlements, setSyncingSettlements] = useState(false);
  const [health, setHealth] = useState(null);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = { status: statusFilter, limit: 100 };
      if (kioskFilter) params.kiosk_id = kioskFilter;
      const r = await axios.get(`${API}/admin/payments/orders`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setOrders(r.data.orders || []);
    } catch (e) {
      toast.error('Failed to load orders');
    }
    setLoading(false);
  };

  const fetchRefunds = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/payments/refunds`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 100 },
      });
      setRefunds(r.data.refunds || []);
    } catch (e) {
      toast.error('Failed to load refunds');
    }
    setLoading(false);
  };

  const fetchSettlements = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/payments/settlements`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 60 },
      });
      setSettlements(r.data || { settlements: [], summary: {} });
    } catch (e) {
      toast.error('Failed to load settlements');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (tab === 'orders') fetchOrders();
    else if (tab === 'refunds') fetchRefunds();
    else if (tab === 'settlements') fetchSettlements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, statusFilter, kioskFilter]);

  useEffect(() => {
    axios.get(`${API}/kiosks`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setKiosksList((r.data || []).map((k) => ({ id: k.id, name: k.name }))))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Client-side column sorting on the loaded page of orders.
  const sortedOrders = useMemo(() => {
    const rows = [...orders];
    const { key, dir } = orderSort;
    rows.sort((a, b) => {
      const av = a[key] ?? '';
      const bv = b[key] ?? '';
      if (typeof av === 'number' || typeof bv === 'number') {
        return dir === 'asc' ? (av || 0) - (bv || 0) : (bv || 0) - (av || 0);
      }
      return dir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
    return rows;
  }, [orders, orderSort]);

  const toggleOrderSort = (key) =>
    setOrderSort((s) => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }));

  const SortTh = ({ colKey, children }) => (
    <th
      className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase cursor-pointer select-none hover:text-charcoal-900"
      onClick={() => toggleOrderSort(colKey)}
      data-testid={`orders-sort-${colKey}`}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {orderSort.key === colKey && (
          orderSort.dir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
        )}
      </span>
    </th>
  );

  // Payment-integration health banner — surfaces missing webhook secret,
  // mock/live status, and anomaly count so the operator doesn't discover
  // a broken webhook only after a customer complaint.
  useEffect(() => {
    if (user?.role !== 'super_admin') return;
    axios.get(`${API}/admin/payments/health`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => setHealth(r.data)).catch(() => setHealth(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const openRefund = (order) => {
    setRefundModal({ order });
    setRefundForm({ amount: order.amount, reason: '' });
  };

  const submitRefund = async () => {
    if (!refundModal) return;
    const amount = Number(refundForm.amount);
    if (!amount || amount <= 0 || amount > refundModal.order.amount) {
      toast.error(`Amount must be between ₹1 and ₹${refundModal.order.amount}`);
      return;
    }
    setSubmittingRefund(true);
    try {
      const r = await axios.post(
        `${API}/admin/payments/${refundModal.order.order_id}/refund`,
        { amount, reason: refundForm.reason },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Refund ${r.data.status} — ₹${amount} on ${refundModal.order.order_id}`);
      setRefundModal(null);
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Refund failed');
    }
    setSubmittingRefund(false);
  };

  const fetchOrderRefunds = async (orderId) => {
    try {
      const r = await axios.get(
        `${API}/admin/payments/${orderId}/refunds`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setOrderDetail({ orderId, refunds: r.data.refunds || [] });
    } catch (e) {
      toast.error('Failed to load refund history');
    }
  };

  const triggerSettlementSync = async () => {
    setSyncingSettlements(true);
    try {
      const r = await axios.post(
        `${API}/admin/payments/sync-settlements`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const d = r.data;
      if (d.skipped_no_live) {
        toast.info('Live mode disabled — settlement sync runs only when Razorpay live keys are configured.');
      } else {
        toast.success(`Fetched ${d.fetched}, stored ${d.stored}, errors ${d.errors}`);
      }
      fetchSettlements();
    } catch (e) {
      toast.error('Sync failed');
    }
    setSyncingSettlements(false);
  };

  return (
    <div className="space-y-6" data-testid="payments-page">
      <div>
        <h1 className="font-serif text-3xl text-charcoal-900">Payments</h1>
        <p className="text-sm text-charcoal-800/60 mt-1">
          Orders, refunds, and Razorpay settlement reconciliation. Refunds in mock mode mark orders refunded immediately; live mode hits Razorpay's refund API through the same circuit-breaker as everything else.
        </p>
      </div>

      {/* Payment health banner — super admin only */}
      {health && (
        <div
          className={`rounded-xl border p-4 flex items-start gap-3 ${
            health.overall_ok
              ? 'bg-green-50 border-green-200'
              : 'bg-amber-50 border-amber-200'
          }`}
          data-testid="payments-health-banner"
        >
          {health.overall_ok ? (
            <ShieldCheck className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
          ) : (
            <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          )}
          <div className="flex-1 min-w-0">
            <div className={`text-sm font-medium ${health.overall_ok ? 'text-green-900' : 'text-amber-900'}`}>
              {health.overall_ok ? 'Payment stack healthy' : 'Payment stack needs attention'}
            </div>
            <ul className="mt-2 text-xs space-y-1">
              {Object.entries(health.checks || {}).map(([k, v]) => (
                <li key={k} className="flex items-start gap-2" data-testid={`payments-health-check-${k}`}>
                  <span className={`inline-block w-2 h-2 rounded-full mt-1.5 ${v.ok ? 'bg-green-500' : 'bg-amber-500'}`} />
                  <span className="text-charcoal-800/80">
                    <span className="font-medium">{k.replace(/_/g, ' ')}:</span> {v.note}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Top tabs */}
      <div className="flex items-center gap-2 border-b border-charcoal-900/10 pb-2">
        {[
          { id: 'orders', label: 'Orders', icon: <Receipt className="w-4 h-4" /> },
          { id: 'refunds', label: 'Refunds', icon: <RotateCcw className="w-4 h-4" /> },
          { id: 'settlements', label: 'Settlements', icon: <Landmark className="w-4 h-4" /> },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`payments-tab-${t.id}`}
            className={`px-4 py-2 rounded-t-lg flex items-center gap-2 text-sm ${
              tab === t.id ? 'bg-charcoal-900 text-cream-50' : 'text-charcoal-800/70 hover:bg-cream-100'
            }`}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* ============ Orders tab ============ */}
      {tab === 'orders' && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            {['paid', 'paid_late', 'refunded', 'failed', 'abandoned', 'created'].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                data-testid={`payments-filter-${s}`}
                className={`px-3 py-1.5 rounded-full text-sm capitalize ${
                  statusFilter === s ? 'bg-charcoal-900 text-cream-50' : 'bg-cream-100 text-charcoal-800/70 hover:bg-cream-200'
                }`}
              >
                {s.replace('_', ' ')}
              </button>
            ))}
            <select
              value={kioskFilter}
              onChange={(e) => setKioskFilter(e.target.value)}
              className="px-3 py-1.5 rounded-lg border border-charcoal-900/10 bg-white text-sm text-charcoal-800/80"
              data-testid="payments-booth-filter"
            >
              <option value="">All booths</option>
              {kiosksList.map((k) => (
                <option key={k.id} value={k.id}>{k.name || k.id}</option>
              ))}
            </select>
            <button onClick={fetchOrders} className="ml-auto text-sm text-charcoal-800/60 hover:text-charcoal-900 flex items-center gap-1">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
          </div>

          <div className="card-dashboard p-0 overflow-hidden">
            {loading ? (
              <div className="text-center py-12 text-charcoal-800/40">Loading…</div>
            ) : orders.length === 0 ? (
              <div className="text-center py-16 text-charcoal-800/40">
                No orders matching <span className="font-mono">{statusFilter}</span>.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="payments-orders-table">
                  <thead className="bg-cream-50">
                    <tr className="text-left">
                      <SortTh colKey="order_id">Order</SortTh>
                      <SortTh colKey="kiosk_name">Booth</SortTh>
                      <SortTh colKey="amount">Amount</SortTh>
                      <SortTh colKey="status">Status</SortTh>
                      <th className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase">Voucher</th>
                      <th
                        className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase"
                        title="Layered proof the order physically printed: spooler confirmation + DNP media-counter delta (paper consumed). Hover a badge for details."
                      >
                        Print
                      </th>
                      <SortTh colKey="created_at">Created</SortTh>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-charcoal-900/5">
                    {sortedOrders.map((o) => (
                      <tr key={o.order_id} className="hover:bg-cream-50">
                        <td className="px-4 py-3">
                          <div className="font-mono text-xs">{o.order_id}</div>
                          <div className="text-[10px] text-charcoal-800/40">
                            qty {o.quantity}
                            {o.imported && <span className="ml-1.5 px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-sans" data-testid={`imported-badge-${o.order_id}`}>imported</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-charcoal-800/80" data-testid={`order-booth-${o.order_id}`}>
                          {o.kiosk_name || o.kiosk_id || '—'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium">₹{o.amount}</div>
                          {o.discount_amount ? <div className="text-[10px] text-charcoal-800/50">−₹{o.discount_amount} disc.</div> : null}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            o.status === 'paid' ? 'bg-green-100 text-green-800'
                              : o.status === 'paid_late' ? 'bg-amber-100 text-amber-800'
                              : o.status === 'refunded' ? 'bg-purple-100 text-purple-800'
                              : o.status === 'failed' ? 'bg-red-100 text-red-800'
                              : 'bg-charcoal-100 text-charcoal-800/70'
                          }`}>{o.status}</span>
                        </td>
                        <td className="px-4 py-3 text-xs text-charcoal-800/70">
                          {o.voucher_code || '—'}
                        </td>
                        <td className="px-4 py-3">
                          <PrintEvidenceBadge ev={o.print_evidence} orderId={o.order_id} />
                        </td>
                        <td className="px-4 py-3 text-xs text-charcoal-800/60">
                          {o.created_at ? istDateTime(o.created_at) : '—'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2 justify-end">
                            <button
                              onClick={() => fetchOrderRefunds(o.order_id)}
                              data-testid={`order-refunds-${o.order_id}`}
                              className="text-xs px-2 py-1 rounded border border-charcoal-900/10 hover:bg-cream-100"
                              title="Show refund history"
                            >
                              History
                            </button>
                            {(o.status === 'paid' || o.status === 'paid_late') && !o.imported && editable && (
                              <button
                                onClick={() => openRefund(o)}
                                data-testid={`refund-btn-${o.order_id}`}
                                className="text-xs px-3 py-1 rounded bg-charcoal-900 text-cream-50 hover:bg-charcoal-800 flex items-center gap-1"
                              >
                                <RotateCcw className="w-3 h-3" /> Refund
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <ScrollSentinel
              onVisible={() => fetchOrders(true)}
              disabled={loading || loadingMore || !hasMoreOrders}
              testid="orders-scroll-sentinel"
            />
            {loadingMore && tab === 'orders' && (
              <div className="text-center py-3 text-xs text-charcoal-800/40">Loading more orders…</div>
            )}
          </div>
        </div>
      )}

      {/* ============ Refunds tab ============ */}
      {tab === 'refunds' && (
        <div className="card-dashboard p-0 overflow-hidden">
          {loading ? (
            <div className="text-center py-12 text-charcoal-800/40">Loading…</div>
          ) : refunds.length === 0 ? (
            <div className="text-center py-16 text-charcoal-800/40">
              <RotateCcw className="w-10 h-10 text-charcoal-800/20 mx-auto mb-2" />
              No refunds yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="payments-refunds-table">
                <thead className="bg-cream-50">
                  <tr className="text-left">
                    <th className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase">Refund</th>
                    <th className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase">Order</th>
                    <th className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase">Amount</th>
                    <th className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase">Status</th>
                    <th className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase">Reason</th>
                    <th className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase">By</th>
                    <th className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase">When</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-charcoal-900/5">
                  {refunds.map((r) => (
                    <tr key={r.id}>
                      <td className="px-4 py-3 font-mono text-xs">{r.id}</td>
                      <td className="px-4 py-3 font-mono text-xs">{r.order_id}</td>
                      <td className="px-4 py-3 font-medium">₹{r.amount}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          r.status === 'processed' ? 'bg-green-100 text-green-800'
                            : r.status === 'pending' ? 'bg-amber-100 text-amber-800'
                            : 'bg-red-100 text-red-800'
                        }`}>{r.status}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-charcoal-800/70">{r.reason || '—'}</td>
                      <td className="px-4 py-3 text-xs">{r.initiated_by}</td>
                      <td className="px-4 py-3 text-xs text-charcoal-800/60">
                        {istDateTime(r.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <ScrollSentinel
            onVisible={() => fetchRefunds(true)}
            disabled={loading || loadingMore || !hasMoreRefunds}
            testid="refunds-scroll-sentinel"
          />
          {loadingMore && tab === 'refunds' && (
            <div className="text-center py-3 text-xs text-charcoal-800/40">Loading more refunds…</div>
          )}
        </div>
      )}

      {/* ============ Settlements tab ============ */}
      {tab === 'settlements' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <SettlementCard label="Total settled" value={`₹${(settlements.summary.total_settled || 0).toFixed(2)}`} icon={<Banknote className="w-4 h-4 text-green-600" />} />
            <SettlementCard label="Total fees" value={`₹${(settlements.summary.total_fees || 0).toFixed(2)}`} icon={<TrendingUp className="w-4 h-4 text-charcoal-700" />} />
            <SettlementCard label="Total tax" value={`₹${(settlements.summary.total_tax || 0).toFixed(2)}`} icon={<TrendingUp className="w-4 h-4 text-charcoal-700" />} />
            <SettlementCard
              label="High-variance"
              value={settlements.summary.high_variance_count || 0}
              icon={<AlertTriangle className="w-4 h-4 text-amber-600" />}
              danger={(settlements.summary.high_variance_count || 0) > 0}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm text-charcoal-800/60">
              Variance threshold: 1% of gross. Crossings auto-log to <code>payment_anomalies</code>.
            </div>
            {editable && (
            <button
              onClick={triggerSettlementSync}
              disabled={syncingSettlements}
              data-testid="settlements-sync-btn"
              className="bg-charcoal-900 text-cream-50 px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-charcoal-800 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${syncingSettlements ? 'animate-spin' : ''}`} /> Sync now
            </button>
            )}
          </div>

          <div className="card-dashboard p-0 overflow-hidden">
            {loading ? (
              <div className="text-center py-12 text-charcoal-800/40">Loading…</div>
            ) : settlements.settlements.length === 0 ? (
              <div className="text-center py-16 text-charcoal-800/40">
                <Landmark className="w-10 h-10 text-charcoal-800/20 mx-auto mb-2" />
                No settlements stored yet.
                <div className="text-xs mt-1">Auto-syncs daily once Razorpay live keys are configured. Manual sync via the button above.</div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="payments-settlements-table">
                  <thead className="bg-cream-50">
                    <tr className="text-left">
                      <th className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase">Settlement</th>
                      <th className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase">Settled</th>
                      <th className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase">Fees</th>
                      <th className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase">Tax</th>
                      <th className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase">Gross paid</th>
                      <th className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase">Variance</th>
                      <th className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase">When</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-charcoal-900/5">
                    {settlements.settlements.map((s) => (
                      <tr key={s.id} className={s.variance_pct > 1 ? 'bg-amber-50/30' : ''}>
                        <td className="px-4 py-3 font-mono text-xs">{s.razorpay_settlement_id}</td>
                        <td className="px-4 py-3">₹{(s.amount_settled || 0).toFixed(2)}</td>
                        <td className="px-4 py-3 text-charcoal-800/60">₹{(s.fees || 0).toFixed(2)}</td>
                        <td className="px-4 py-3 text-charcoal-800/60">₹{(s.tax || 0).toFixed(2)}</td>
                        <td className="px-4 py-3">₹{(s.gross_paid_in_window || 0).toFixed(2)}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            s.variance_pct > 1 ? 'bg-amber-100 text-amber-900' : 'bg-green-100 text-green-800'
                          }`}>
                            {(s.variance_pct || 0).toFixed(2)}%
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-charcoal-800/60">
                          {s.settled_at ? istDateTime(s.settled_at) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ============ Refund modal ============ */}
      {refundModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="refund-modal">
          <div className="bg-cream-50 rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-xl">Refund order</h3>
              <button onClick={() => setRefundModal(null)} className="text-charcoal-800/60 hover:text-charcoal-900">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3">
              <div className="text-sm bg-cream-100 rounded p-3">
                <div className="font-mono text-xs">{refundModal.order.order_id}</div>
                <div>Original amount: <span className="font-medium">₹{refundModal.order.amount}</span></div>
              </div>
              <div>
                <label className="text-xs text-charcoal-800/60">Refund amount (₹)</label>
                <input
                  type="number"
                  value={refundForm.amount}
                  onChange={(e) => setRefundForm({ ...refundForm, amount: e.target.value })}
                  data-testid="refund-amount-input"
                  className="w-full px-3 py-2 border border-charcoal-900/10 rounded-lg mt-1"
                  max={refundModal.order.amount}
                  min={1}
                />
                <div className="text-[10px] text-charcoal-800/50 mt-1">Max: ₹{refundModal.order.amount}</div>
              </div>
              <div>
                <label className="text-xs text-charcoal-800/60">Reason (internal note)</label>
                <input
                  value={refundForm.reason}
                  onChange={(e) => setRefundForm({ ...refundForm, reason: e.target.value })}
                  data-testid="refund-reason-input"
                  placeholder="Customer requested / printer jam / duplicate charge"
                  className="w-full px-3 py-2 border border-charcoal-900/10 rounded-lg mt-1"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setRefundModal(null)}
                  className="flex-1 px-4 py-2 rounded-lg border border-charcoal-900/10 hover:bg-cream-100"
                >
                  Cancel
                </button>
                <button
                  onClick={submitRefund}
                  disabled={submittingRefund}
                  data-testid="refund-submit-btn"
                  className="flex-1 px-4 py-2 rounded-lg bg-charcoal-900 text-cream-50 hover:bg-charcoal-800 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submittingRefund ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  Issue refund
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============ Order refund history modal ============ */}
      {orderDetail && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setOrderDetail(null)}>
          <div className="bg-cream-50 rounded-lg p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-xl">Refund history</h3>
              <button onClick={() => setOrderDetail(null)} className="text-charcoal-800/60 hover:text-charcoal-900"><X className="w-5 h-5" /></button>
            </div>
            <div className="text-xs font-mono mb-3 text-charcoal-800/60">{orderDetail.orderId}</div>
            {orderDetail.refunds.length === 0 ? (
              <div className="text-sm text-charcoal-800/50 py-4 text-center">No refunds on this order.</div>
            ) : (
              <div className="space-y-2">
                {orderDetail.refunds.map((r) => (
                  <div key={r.id} className="border border-charcoal-900/10 rounded p-3 text-sm">
                    <div className="flex justify-between mb-1">
                      <span className="font-medium">₹{r.amount}</span>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        r.status === 'processed' ? 'bg-green-100 text-green-800'
                          : r.status === 'pending' ? 'bg-amber-100 text-amber-800'
                          : 'bg-red-100 text-red-800'
                      }`}>{r.status}</span>
                    </div>
                    <div className="text-xs text-charcoal-800/60">
                      {r.reason || 'No reason recorded'}
                    </div>
                    <div className="text-[10px] text-charcoal-800/40 mt-1">
                      by {r.initiated_by} · {istDateTime(r.created_at)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const EVIDENCE_STYLES = {
  paper_verified: ['bg-green-100 text-green-800', 'Printed · paper ✓'],
  partial_paper: ['bg-amber-100 text-amber-800', 'Partial paper'],
  counter_unchanged: ['bg-red-100 text-red-800', 'Spooled, no paper'],
  spooler_only: ['bg-amber-100 text-amber-800', 'Spooler only'],
  dispatch_failed: ['bg-red-100 text-red-800', 'Print failed'],
};

const PrintEvidenceBadge = ({ ev, orderId }) => {
  if (!ev) {
    return (
      <span
        className="text-xs text-charcoal-800/30"
        title="No print evidence — order predates evidence tracking or booth software not yet updated (needs kiosk v3.17.0+)"
        data-testid={`print-evidence-${orderId}`}
      >
        —
      </span>
    );
  }
  const [cls, label] = EVIDENCE_STYLES[ev.verdict] || ['bg-charcoal-100 text-charcoal-800/70', ev.verdict];
  const tip = [
    `Verdict: ${ev.verdict}`,
    `Copies: ${ev.spooled_copies ?? '?'}/${ev.copies ?? '?'} spooled`,
    ev.counter_before ? `Media before: ${ev.counter_before.remaining} left (${ev.counter_before.source || 'reading'})` : 'Media before: unavailable',
    ev.counter_after ? `Media after: ${ev.counter_after.remaining} left` : 'Media after: unavailable',
    ev.counter_delta != null ? `Paper consumed: ${ev.counter_delta}` : null,
    ev.paper_latency_ms != null ? `Paper out in: ${Math.round(ev.paper_latency_ms / 1000)}s after dispatch` : null,
    ev.counter_note ? `Note: ${ev.counter_note}` : null,
    ev.dispatched_at ? `Dispatched: ${istDateTime(ev.dispatched_at)}` : null,
    ev.printer_name ? `Printer: ${ev.printer_name}` : null,
  ].filter(Boolean).join('\n');
  return (
    <span
      title={tip}
      data-testid={`print-evidence-${orderId}`}
      className={`text-xs px-2 py-0.5 rounded cursor-help whitespace-nowrap ${cls}`}
    >
      {label}
    </span>
  );
};

const SettlementCard = ({ label, value, icon, danger }) => (
  <div className={`card-dashboard ${danger ? 'border-2 border-amber-500 bg-amber-50/50' : ''}`}>
    <div className="text-xs uppercase text-charcoal-800/50 tracking-wider flex items-center gap-1.5">
      {icon} {label}
    </div>
    <div className="font-serif text-2xl mt-1">{value}</div>
  </div>
);

export default Payments;
